import { defineFilterSpec, enumField, multiField, text } from '@/lib/api/filter-spec';

import { storedDeviceAlertFiltersSchema } from './schemas';
import type { CustomerDeviceAlertFilterFormValues, CustomerDeviceAlertFilters } from './types';

/** Device Alerts filters (118646). Applied keys are the wire params, so no `param` renames. */
const spec = defineFilterSpec<CustomerDeviceAlertFilters, CustomerDeviceAlertFilterFormValues>([
  text('search'),
  multiField('alertTypes'),
  multiField('deviceIds'),
  multiField('deviceType'),
  multiField('category'),
  enumField('logDateFrom'),
  enumField('logDateTo'),
]);

export const deviceAlertToListQuery = spec.toListQuery;
export const deviceAlertToDefaults = spec.toDefaults;
export const deviceAlertToFilters = spec.toFilters;
export const deviceAlertHasActiveFilters = spec.hasActiveFilters;

/** ADR-0016 amendment: applied filters on this one tab survive a reload (a reload runs no unmount
 *  cleanup) and clear on a tab switch (the tab's unmount effect calls `clear…`). Every access is
 *  wrapped — private mode or a full quota degrades to the in-memory React state. */
const key = (customerId: string) => `customer-device-alerts:${customerId}`;

export function readStoredDeviceAlertFilters(customerId: string): CustomerDeviceAlertFilters {
  try {
    const raw = sessionStorage.getItem(key(customerId));
    const parsed = raw ? storedDeviceAlertFiltersSchema.safeParse(JSON.parse(raw)) : null;
    return parsed?.success ? parsed.data : {};
  } catch {
    return {};
  }
}

export function writeStoredDeviceAlertFilters(
  customerId: string,
  { search: _search, ...filters }: CustomerDeviceAlertFilters,
): void {
  try {
    if (deviceAlertHasActiveFilters(filters)) {
      sessionStorage.setItem(key(customerId), JSON.stringify(filters));
    } else {
      sessionStorage.removeItem(key(customerId));
    }
  } catch {
    // memory fallback — the caller's state still holds the filters
  }
}

export function clearStoredDeviceAlertFilters(customerId: string): void {
  try {
    sessionStorage.removeItem(key(customerId));
  } catch {
    // nothing to clear
  }
}
